import { createContext, useContext, useEffect, useState } from 'react';

import { LSKeys, LocalStorage } from '@core/localStorage';
import { ThemeMode, ThemeModeConstants } from '@helpers/theme.helper';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: ThemeMode;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
};

const initialState: ThemeProviderState = {
  theme: ThemeModeConstants.SYSTEM,
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = ThemeModeConstants.SYSTEM,
  storageKey = 'vite-ui-theme',
  ...props
}: ThemeProviderProps) {
  const FORCE_LIGHT = true;
  const [theme, setTheme] = useState<ThemeMode>(
    () => (LocalStorage.get(LSKeys.THEME_MODE) as ThemeMode) || defaultTheme
  );

  useEffect(() => {
    const root = document.body; //window.document.documentElement;
    root.classList.remove('light', 'dark');

    if (FORCE_LIGHT) {
      root.classList.add('light');
      return;
    }

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
        .matches
        ? 'dark'
        : 'light';
      root.classList.add(systemTheme);
      return;
    }

    root.classList.add(theme);
  }, [theme]);

  const value = {
    theme: FORCE_LIGHT ? ThemeModeConstants.LIGHT : theme,
    setTheme: (theme: ThemeMode) => {
      LocalStorage.set(LSKeys.THEME_MODE, theme);
      setTheme(theme);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider');

  return context;
};
